import gql from 'graphql-tag'

export const getTheaterSubscriptions = gql`
  query getTheaterSubscriptions($organizationSlug: String!) {
    getTheaterSubscriptions(organizationSlug: $organizationSlug) {
      id
      name
      price
      valid_from
      valid_to
      active
      venueId
      coverUrl
      description
      createdAt
      updatedAt
      schedule_sales_availability
      available_from
      available_to
      enable_pre_order
      pre_order_days
      hasPurchaseHistory
      generalAdmissionCapacity {
        capacityLimit includedEventCount firstPerformanceAt readiness
        performances { eventId title startsAt quantity }
      }
      venue {
        id
        name
        seats
        predefinedSections
      }
      subscriptionPurchases {
        id
        active
      }
    }
  }
`

export const GET_THEATER_SUBSCRIPTION_REPORT_OPTIONS = gql`
  query GetTheaterSubscriptionReportOptions($organizationSlug: String!) {
    getTheaterSubscriptionReportOptions(organizationSlug: $organizationSlug) {
      id
      name
    }
  }
`

export const getTheaterSubscription = gql`
  query getTheaterSubscription($id: Int!) {
    getTheaterSubscription(id: $id) {
      id
      name
      price
      valid_from
      valid_to
      active
      coverUrl
      createdAt
      updatedAt
      schedule_sales_availability
      available_from
      available_to
      enable_pre_order
      pre_order_days
      hasPurchaseHistory
      generalAdmissionCapacity {
        capacityLimit includedEventCount firstPerformanceAt readiness
        performances { eventId title startsAt quantity }
      }
      subscriptionPurchases {
        id
        active
        createdAt
      }
    }
  }
`

export const getTheaterSubscriptionPurchases = gql`
  query getTheaterSubscriptionPurchases($organizationSlug: String!) {
    getTheaterSubscriptionPurchases(organizationSlug: $organizationSlug) {
      id
      customerName
      customerAddress
      customerEmail
      active
      createdAt
      ticketLocation
      theaterSubscription {
        id
        name
        valid_from
        valid_to
        description
      }
    }
  }
`

// Load eligible events once per plan, rather than once for every subscriber.
export const getTheaterSubscriptionEmailPlans = gql`
  query getTheaterSubscriptionEmailPlans($organizationSlug: String!) {
    getTheaterSubscriptions(organizationSlug: $organizationSlug) {
      id
      theaterSubscriptionEvents {
        eventId: event_id
        event {
          id
          name: title
          from
        }
      }
    }
  }
`

export const getTheaterSubscriptionPurchasesByUser = gql`
  query getTheaterSubscriptionPurchasesByUser($userId: Int!) {
    getTheaterSubscriptionPurchasesByUser(userId: $userId) {
      id
      active
      ticketLocation
      theaterSubscription {
        id
        organizationId
        venueId
        valid_from
        valid_to
      }
    }
  }
`

export const searchTheaterSubscriptionPurchases = gql`
  query SearchTheaterSubscriptionPurchases(
    $organizationSlug: String!
    $searchTerm: String
    $subscriptionId: Int
    $season: String
    $pagination: Pagination
  ) {
    searchTheaterSubscriptionPurchases(
      organizationSlug: $organizationSlug
      searchTerm: $searchTerm
      subscriptionId: $subscriptionId
      season: $season
      pagination: $pagination
    ) {
      rows {
        id
        customerName
        customerEmail
        customerPhone
        customerAddress
        active
        createdAt
        ticketLocation
        theaterSubscription {
          id
          name
          valid_from
          valid_to
          description
          venue {
            id
            name
            seats
            predefinedSections
          }
        }
      }
      count
    }
  }
`

export const getFloorPlanForTheaterSubscription = gql`
  query getFloorPlanForTheaterSubscription($theaterSubscriptionId: ID!) {
    getFloorPlanForTheaterSubscription(theaterSubscriptionId: $theaterSubscriptionId) {
      venueImageUrl
      seatStates
      svgSeatId
    }
  }
`

export const createTheaterSubscription = gql`
  mutation createTheaterSubscription($input: TheaterSubscriptionInput!) {
    createTheaterSubscription(input: $input) {
      id
      name
      price
      valid_from
      valid_to
      active
      coverUrl
      schedule_sales_availability
      available_from
      available_to
      enable_pre_order
      pre_order_days
      description
    }
  }
`

export const updateTheaterSubscription = gql`
  mutation updateTheaterSubscription($input: TheaterSubscriptionUpdateInput!) {
    updateTheaterSubscription(input: $input) {
      id
      name
      price
      valid_from
      valid_to
      active
      coverUrl
      description
      venueId
      hasPurchaseHistory
      generalAdmissionCapacity {
        capacityLimit includedEventCount firstPerformanceAt readiness
        performances { eventId title startsAt quantity }
      }
      venue {
        id
        name
        seats
        predefinedSections
      }
      subscriptionPurchases {
        id
        active
      }
      schedule_sales_availability
      available_from
      available_to
      enable_pre_order
      pre_order_days
    }
  }
`

export const deleteTheaterSubscription = gql`
  mutation deleteTheaterSubscription($id: Int!, $organizationSlug: String!) {
    deleteTheaterSubscription(id: $id, organizationSlug: $organizationSlug)
  }
`

export const createTheaterSubscriptionPurchase = gql`
  mutation createTheaterSubscriptionPurchase($input: TheaterSubscriptionPurchaseInput!) {
    createTheaterSubscriptionPurchase(input: $input) {
      id
      customerName
      active
    }
  }
`

export const updateTheaterSubscriptionPurchase = gql`
  mutation updateTheaterSubscriptionPurchase($id: Int!, $active: Boolean!, $organizationSlug: String!) {
    updateTheaterSubscriptionPurchase(id: $id, active: $active, organizationSlug: $organizationSlug) {
      id
      active
    }
  }
`

export const GET_THEATER_SUBSCRIPTION_FINANCE_SUMMARY = gql`
  query GetTheaterSubscriptionFinanceSummary($filters: TheaterSubscriptionFinanceFilterInput) {
    getTheaterSubscriptionFinanceSummary(filters: $filters) {
      organizationName
      subscriptionName
      grossAmount
      netAmount
      vatAmount
      totalQuantity
      orderCount
      onlineGrossAmount
      offlineGrossAmount
    }
  }
`

export const GET_THEATER_SUBSCRIPTION_FINANCE_LEDGER = gql`
  query GetTheaterSubscriptionFinanceLedger($filters: TheaterSubscriptionFinanceFilterInput) {
    getTheaterSubscriptionFinanceLedger(filters: $filters) {
      orderId
      orderUuid
      createdAt
      invoiceNumber
      fiscalJir
      paymentType
      paymentChannel
      organizationSlug
      organizationName
      subscriptionId
      subscriptionName
      quantity
      unitPrice
      grossAmount
      netAmount
      vatAmount
      vatRate
      currency
    }
  }
`

export const deleteTheaterSubscriptionPurchase = gql`
  mutation deleteTheaterSubscriptionPurchase($id: Int!, $organizationSlug: String!) {
    deleteTheaterSubscriptionPurchase(id: $id, organizationSlug: $organizationSlug)
  }
`

export const renewTheaterSubscriptionPurchase = gql`
  mutation renewTheaterSubscriptionPurchase($purchaseId: Int!, $organizationSlug: String!) {
    renewTheaterSubscriptionPurchase(purchaseId: $purchaseId, organizationSlug: $organizationSlug) {
      id
      active
      ticketLocation
    }
  }
`

export const extendHalfYearSubscription = gql`
  mutation extendHalfYearSubscription($id: Int!) {
    extendHalfYearSubscription(id: $id) {
      id
      customerName
      theaterSubscription {
        id
        name
      }
    }
  }
`

// Theater Subscription Event GraphQL operations
export const getTheaterSubscriptionEvents = gql`
  query getTheaterSubscriptionEvents($organizationSlug: String!) {
    getTheaterSubscriptionEvents(organizationSlug: $organizationSlug) {
      id
      eventId
      event {
        id
        name
        from
      }
    }
  }
`

export const getEventTemplateSubscriptions = gql`
  query getEventTemplateSubscriptions($event_template_id: Int!) {
    getEventTemplateSubscriptions(event_template_id: $event_template_id) {
      id
      theater_subscription {
        id
        name
        price
      }
    }
  }
`

export const addEventTemplateToSubscription = gql`
  mutation addEventTemplateToSubscription($input: TheaterSubscriptionEventInput!) {
    addEventTemplateToSubscription(input: $input) {
      id
    }
  }
`

export const updateTheaterSubscriptionEvent = gql`
  mutation updateTheaterSubscriptionEvent($input: TheaterSubscriptionEventUpdateInput!) {
    updateTheaterSubscriptionEvent(input: $input) {
      id
    }
  }
`

export const removeEventTemplateFromSubscription = gql`
  mutation removeEventTemplateFromSubscription($id: Int!) {
    removeEventTemplateFromSubscription(id: $id)
  }
`

export const addAllEventsFromTemplateToSubscription = gql`
  mutation addAllEventsFromTemplateToSubscription($event_template_id: Int!) {
    addAllEventsFromTemplateToSubscription(event_template_id: $event_template_id)
  }
`

// New query to fetch events for a specific theater subscription purchase
export const getTheaterSubscriptionEventsForPurchase = gql`
  query GetTheaterSubscriptionEventsForPurchase($purchaseId: Int!, $organizationSlug: String!) {
    getTheaterSubscriptionEventsForPurchase(
      purchaseId: $purchaseId
      organizationSlug: $organizationSlug
    ) {
      id
      name
      date
      status
      purchaseId
      subscriberSeatState
      interactionStatus
    }
  }
`

export const getFloorPlanForSubscriptionPurchase = gql`
  query getFloorPlanForSubscriptionPurchase($purchaseId: Int!, $organizationSlug: String!) {
    getFloorPlanForSubscriptionPurchase(
      purchaseId: $purchaseId
      organizationSlug: $organizationSlug
    ) {
      venueImageUrl
      svgSeatId
    }
  }
`

export const releaseSeatForSubscriptionPurchaseEvent = gql`
  mutation releaseSeatForSubscriptionPurchaseEvent($purchaseId: Int!, $eventId: Int!) {
    releaseSeatForSubscriptionPurchaseEvent(purchaseId: $purchaseId, eventId: $eventId) {
      availability
    }
  }
`
export const releaseSeatForAllUpcomingPerformances = gql`
  mutation releaseSeatForAllUpcomingPerformances($theaterSubscriptionPurchaseId: Int!) {
    releaseSeatForAllUpcomingPerformances(
      theaterSubscriptionPurchaseId: $theaterSubscriptionPurchaseId
    )
  }
`
export const reassignSeatForSubscriptionPurchaseEvent = gql`
  mutation reassignSeatForSubscriptionPurchaseEvent($purchaseId: Int!, $eventId: Int!) {
    reassignSeatForSubscriptionPurchaseEvent(purchaseId: $purchaseId, eventId: $eventId) {
      eventId # Or other fields from VenueAvailability you might need to confirm
      availability
    }
  }
`

export const resendTheaterSubscriptionConfirmation = gql`
  mutation resendTheaterSubscriptionConfirmation($purchaseId: Int!, $organizationSlug: String!, $lang: String) {
    resendTheaterSubscriptionConfirmation(purchaseId: $purchaseId, organizationSlug: $organizationSlug, lang: $lang)
  }
`

export const getAvailableTheaterSubscriptionsPublic = gql`
  query getAvailableTheaterSubscriptionsPublic($organizationSlug: String!) {
    getAvailableTheaterSubscriptionsPublic(organizationSlug: $organizationSlug) {
      id
      name
      price
      coverUrl
      valid_from
      valid_to
      active
      description
      isOwnedByCurrentUser
      remainingQuantity
      soldOut
      schedule_sales_availability
      available_from
      available_to
      enable_pre_order
      pre_order_days
      organization {
        id
        publicName
        legalName
        slug
      }
      venue {
        id
        name
        seats
        predefinedSections
      }
    }
  }
`

export const getTheaterSubscriptionPublic = gql`
  query getTheaterSubscriptionPublic($subscriptionId: Int!) {
    getTheaterSubscriptionPublic(subscriptionId: $subscriptionId) {
      id
      name
      price
      valid_from
      valid_to
      active
      description
      remainingQuantity
      soldOut
      schedule_sales_availability
      available_from
      available_to
      enable_pre_order
      pre_order_days
      organization {
        id
        publicName
        legalName
        slug
      }
      venue {
        id
        name
        seats
        predefinedSections
      }
    }
  }
`

export const createTheaterSubscriptionPurchasePublic = gql`
  mutation createTheaterSubscriptionPurchasePublic(
    $input: TheaterSubscriptionPublicPurchaseInput!
  ) {
    createTheaterSubscriptionPurchasePublic(input: $input) {
      purchase {
        id
        customerName
        customerEmail
        customerAddress
        customerPhone
        ticketLocation
        active
        qrCode
        createdAt
        theaterSubscription {
          id
          name
          price
          valid_from
          valid_to
          description
        }
      }
      order {
        id
        uuid
        status
        price
        buyer
        createdAt
      }
      purchases {
        id
        orderId
        attendeeId
        orderItemId
      }
    }
  }
`

export const GET_THEATER_SUBSCRIPTION_EVENTS = gql`
  query getTheaterSubscriptionEvents($organizationSlug: String!) {
    getTheaterSubscriptionEvents(organizationSlug: $organizationSlug) {
      id
      eventId
      event {
        id
        name
        from
      }
    }
  }
`

export const GET_THEATER_SUBSCRIPTION_FLOOR_PLAN_OVERVIEW = gql`
  query GetTheaterSubscriptionFloorPlanOverview(
    $organizationSlug: String!
    $eventId: ID
    $search: String
    $season: String
  ) {
    getTheaterSubscriptionFloorPlanOverview(
      organizationSlug: $organizationSlug
      eventId: $eventId
      search: $search
      season: $season
    ) {
      seasons
      appliedSeason
      venue {
        imageUrl
        seatIds
        predefinedSeats
        audienceCapacity
      }
      plans {
        id
        name
        color
        totalSeats
      }
      assignments {
        id
        seatId
        subscriberName
        subscriberEmail
        qrCode
        planId
        planName
        planColor
        createdAt
        interactionStatus
      }
      events {
        id
        title
        date
        seatSummary {
          sold
          hold
          available
          disabled
          total
          statusCounts
        }
      }
      totals {
        totalSeats
        occupiedSeats
        freeSeats
      }
    }
  }
`

export const PREVIEW_THEATER_SUBSCRIPTION_CAPACITY = gql`
  query PreviewTheaterSubscriptionCapacity($input: TheaterSubscriptionCapacityPreviewInput!) {
    previewTheaterSubscriptionCapacity(input: $input) {
      capacityLimit includedEventCount firstPerformanceAt readiness
      performances { eventId title startsAt quantity }
    }
  }
`
